import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from './axiosConfig';
import UnconfirmedLiabilitiesPanel from './UnconfirmedLiabilitiesPanel';
import { parseHmrcErrorDetail, getDisplayMessage } from './hmrcErrorParsing';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Alert,
    AlertTitle,
    Grid,
    Divider,
    Checkbox,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
    Stepper,
    Step,
    StepLabel
} from '@mui/material';
import {
    AccountBalance as HMRCIcon,
    ArrowBack,
    CheckCircle,
    Send as SubmitIcon,
    Warning as WarningIcon,
    Receipt as ReceiptIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
 
const VATSubmission = () => {
    const navigate = useNavigate();
    // CHANGED: was { periodKey }. The route param itself must also change
    // in the router config from /vat/submit/:periodKey to
    // /vat/submit/:obligationId — see routing note at bottom of this file.
    const { obligationId } = useParams();
 
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [vatData, setVatData] = useState(null);
    const [error, setError] = useState(null);
    const [rejectionDetail, setRejectionDetail] = useState(null); // { code, message } | null
    const [declarationChecked, setDeclarationChecked] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submissionResult, setSubmissionResult] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [blockedUnconfirmed, setBlockedUnconfirmed] = useState(null); // array or null
    const [checkingUnconfirmed, setCheckingUnconfirmed] = useState(true);
    const [liabilityConfirmError, setLiabilityConfirmError] = useState(null);
 
    const steps = ['Review Figures', 'Confirm Declaration', 'Submit to HMRC'];
 
    useEffect(() => {
        fetchVATData();
        checkUnconfirmedLiabilities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obligationId]);
 
    const checkUnconfirmedLiabilities = async () => {
        setCheckingUnconfirmed(true);
        try {
            const res = await api.get('/hmrc/unconfirmed-liabilities');
            if (res.data?.unconfirmed_count > 0) {
                setBlockedUnconfirmed(res.data.unconfirmed_submissions || []);
            } else {
                setBlockedUnconfirmed(null);
            }
        } catch (err) {
            console.error('Failed to check unconfirmed liabilities', err);
            // Fail open here: don't block submission on a failed check itself.
            // The backend's own gate on /hmrc/submit is the authoritative check.
        } finally {
            setCheckingUnconfirmed(false);
        }
    };
 
    const fetchVATData = async () => {
        try {
            setLoading(true);
            // CHANGED: was /hmrc/calculate-return/${periodKey}
            const res = await api.get(`/hmrc/obligations/${obligationId}/calculate-return`);
            setVatData(res.data);
        } catch (err) {
            console.error('Failed to fetch VAT data', err);
            setError(err.response?.data?.error || 'Failed to calculate VAT return');
        } finally {
            setLoading(false);
        }
    };
 
    const handleSubmit = async () => {
        if (!declarationChecked) {
            setError('You must confirm the declaration before submitting');
            return;
        }
 
        setConfirmDialog(false);
        setSubmitting(true);
        setError(null);
        setRejectionDetail(null);
 
        try {
            // CHANGED: sends obligation_id, not period_key. box1-9 are no
            // longer sent at all — the backend recalculates them itself
            // from the obligation and ignores anything sent here, but
            // there's no reason to send stale client-side figures either.
            const res = await api.post('/hmrc/submit', {
                obligation_id: obligationId,
                declaration_confirmed: true
            });
 
            const submission = res.data.submission;
            setSubmissionResult(submission);
            setSubmitted(true);
            setActiveStep(2);
 
            // Immediately prompt to confirm the liability for the submission
            // just created. This call is fire-and-forget against the success
            // screen — if it fails, the persistent banner (checked on next
            // load of VatDashboard / VATSubmission) will still catch it, so
            // the user isn't silently left with an unconfirmed liability.
            if (submission?.id) {
                try {
                    await api.post(`/hmrc/submissions/${submission.id}/confirm-liability`);
                } catch (confirmErr) {
                    console.error('Failed to auto-confirm liability after submission', confirmErr);
                    setLiabilityConfirmError(
                        'Your VAT return was submitted successfully, but the liability could not be confirmed automatically. You will be prompted to confirm it next time you open VAT Returns.'
                    );
                }
            }
        } catch (err) {
            console.error('Submission failed', err);
 
            if (err.response?.status === 409 && err.response?.data?.error === 'BLOCKED_UNCONFIRMED_LIABILITY') {
                // The 409 body only carries submission_id/period_start/period_end —
                // re-fetch from GET /hmrc/unconfirmed-liabilities for the full
                // shape (box5_net_vat_due, submitted_at) the takeover panel needs.
                await checkUnconfirmedLiabilities();
            } else {
                // HMRC rejections carry a structured reason at
                // err.response.data.details (e.g. { code, message } or
                // { errors: [...] }) — see hmrc_routes.py submit_vat_return.
                // Parse it via the same shared helper VatSubmissionHistory.js
                // uses, so a structured reason is shown instead of the flat
                // "HMRC rejected the submission" banner wherever one exists.
                const detail = parseHmrcErrorDetail(err.response?.data?.details);
                setRejectionDetail(detail);
                setError(err.response?.data?.error || 'Failed to submit VAT return to HMRC');
            }
        } finally {
            setSubmitting(false);
        }
    };
 
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };
 
    const formatWholePounds = (amount) => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };
 
    if (loading || checkingUnconfirmed) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }} role="status" aria-label="Loading VAT data">
                <CircularProgress />
            </Box>
        );
    }
 
    // Hard block: a prior accepted submission has no confirmed liability.
    // This takes precedence over both the review screen and the success
    // screen — per Andrew's explicit decision, this is not skippable or
    // dismissible, and not a generic error toast.
    if (blockedUnconfirmed && blockedUnconfirmed.length > 0) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 } }}>
                <UnconfirmedLiabilitiesPanel
                    submissions={blockedUnconfirmed}
                    variant="takeover"
                    onAllConfirmed={() => {
                        setBlockedUnconfirmed(null);
                        // Re-check VAT data in case anything changed while blocked
                        fetchVATData();
                    }}
                />
            </Box>
        );
    }
 
    // Success screen
    if (submitted && submissionResult) {
        return (
            <Box>
                <Paper
                    elevation={0}
                    sx={{
                        background: 'linear-gradient(135deg, #059669, #047857)',
                        color: 'white',
                        p: 4,
                        mb: 4,
                        borderRadius: 0
                    }}
                >
                    <Container maxWidth="md">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
                            <CheckCircle sx={{ fontSize: 48 }} aria-hidden="true" />
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                                VAT Return Submitted Successfully
                            </Typography>
                        </Box>
                    </Container>
                </Paper>
 
                <Container maxWidth="md">
                    {liabilityConfirmError && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            {liabilityConfirmError}
                        </Alert>
                    )}
                    <Card elevation={0} sx={{ border: '1px solid #e5e7eb' }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <Typography variant="h6" component="h2" gutterBottom>
                                    HMRC has accepted your VAT return
                                </Typography>
                                <Typography color="text.secondary">
                                    Keep this receipt for your records
                                </Typography>
                            </Box>
 
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary" component="span" id="receipt-label">
                                            HMRC Receipt ID
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontFamily: 'monospace' }} aria-labelledby="receipt-label">
                                            {submissionResult.hmrc_receipt_id || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary" component="span" id="charge-label">
                                            Charge Reference
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontFamily: 'monospace' }} aria-labelledby="charge-label">
                                            {submissionResult.charge_ref_number || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary" component="span" id="period-label">
                                            Period
                                        </Typography>
                                        <Typography variant="h6" aria-labelledby="period-label">
                                            {new Date(submissionResult.period_start).toLocaleDateString('en-GB')} - {new Date(submissionResult.period_end).toLocaleDateString('en-GB')}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {/* Fixed: Changed #dc2626 to #b91c1c for better contrast on #fef2f2 background */}
                                    <Box sx={{ p: 2, bgcolor: submissionResult.box5_net_vat_due >= 0 ? '#fef2f2' : '#f0fdf4', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary" component="span" id="amount-label">
                                            {submissionResult.box5_net_vat_due >= 0 ? 'Amount Due to HMRC' : 'Amount Due to You'}
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: submissionResult.box5_net_vat_due >= 0 ? '#b91c1c' : '#047857' }} aria-labelledby="amount-label">
                                            {formatCurrency(Math.abs(submissionResult.box5_net_vat_due))}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
 
                            <Divider sx={{ my: 4 }} />
 
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ReceiptIcon />}
                                    onClick={() => navigate('/vat/history')}
                                >
                                    View All Submissions
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/settings/hmrc')}
                                >
                                    Back to HMRC Settings
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        );
    }
 
    return (
        <Box>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #1e3a5f, #0d1b2a)',
                    color: 'white',
                    p: 4,
                    mb: 4,
                    borderRadius: 0
                }}
            >
                <Container maxWidth="md">
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/settings/hmrc')}
                        sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                        aria-label="Go back to HMRC Settings"
                    >
                        Back to HMRC Settings
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <HMRCIcon sx={{ fontSize: 28 }} aria-hidden="true" />
                        </Box>
                        <Box>
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Submit VAT Return
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                {vatData && `${new Date(vatData.period_start).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })} - ${new Date(vatData.period_end).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`}
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Paper>
 
            <Container maxWidth="md">
                {/* Stepper */}
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
 
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3 }}
                        onClose={() => { setError(null); setRejectionDetail(null); }}
                    >
                        {rejectionDetail ? (
                            <>
                                {getDisplayMessage(rejectionDetail)}
                                {rejectionDetail.code && (
                                    <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.8 }}>
                                        HMRC error code: {rejectionDetail.code}
                                    </Typography>
                                )}
                            </>
                        ) : (
                            error
                        )}
                    </Alert>
                )}
 
                {vatData && (
                    <Card elevation={0} sx={{ border: '1px solid #e5e7eb', mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            {/* Fixed: Changed from variant="h6" (renders as h6) to component="h2" for proper heading hierarchy */}
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
                                VAT Return Summary
                            </Typography>
 
                            <Alert severity="info" sx={{ mb: 3 }}>
                                <AlertTitle>Review these figures carefully</AlertTitle>
                                These have been calculated from your income and expenditure records in SalonLogic Pro.
                            </Alert>
 
                            {/* Fixed: Added TableHead with proper th elements for accessibility */}
                            <Table aria-label="VAT return boxes summary">
                                <TableHead>
                                    <TableRow sx={{ visibility: 'collapse' }}>
                                        <TableCell component="th" scope="col">Box Number</TableCell>
                                        <TableCell component="th" scope="col">Description</TableCell>
                                        <TableCell component="th" scope="col" align="right">Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>Box 1</TableCell>
                                        <TableCell>VAT due on sales and other outputs</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(vatData.boxes.box1)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>Box 2</TableCell>
                                        <TableCell>VAT due on acquisitions from EU</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(vatData.boxes.box2)}</TableCell>
                                    </TableRow>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>Box 3</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>Total VAT due (Box 1 + Box 2)</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(vatData.boxes.box3)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>Box 4</TableCell>
                                        <TableCell>VAT reclaimed on purchases</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(vatData.boxes.box4)}</TableCell>
                                    </TableRow>
                                    {/* Fixed: Changed #dc2626 to #b91c1c for WCAG AA contrast compliance (4.5:1 ratio) */}
                                    <TableRow sx={{ bgcolor: vatData.boxes.box5 >= 0 ? '#fef2f2' : '#f0fdf4' }}>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>Box 5</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            {vatData.boxes.box5 >= 0 ? 'Net VAT to pay HMRC' : 'Net VAT due to you'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: vatData.boxes.box5 >= 0 ? '#b91c1c' : '#047857' }}>
                                            {formatCurrency(Math.abs(vatData.boxes.box5))}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>Box 6</TableCell>
                                        <TableCell>Total sales excluding VAT</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatWholePounds(vatData.boxes.box6)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>Box 7</TableCell>
                                        <TableCell>Total purchases excluding VAT</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatWholePounds(vatData.boxes.box7)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>Box 8</TableCell>
                                        <TableCell>Total value of supplies to EU</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatWholePounds(vatData.boxes.box8)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>Box 9</TableCell>
                                        <TableCell>Total value of acquisitions from EU</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatWholePounds(vatData.boxes.box9)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}
 
                {/* Declaration */}
                <Card elevation={0} sx={{ border: '2px solid #fbbf24', bgcolor: '#fffbeb', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <WarningIcon sx={{ color: '#d97706', mt: 0.5 }} aria-hidden="true" />
                            <Box>
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
                                    Declaration
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    When you submit this VAT information you are making a legal declaration that the information is true and complete. A false declaration can result in prosecution.
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={declarationChecked}
                                            onChange={(e) => {
                                                setDeclarationChecked(e.target.checked);
                                                if (e.target.checked) setActiveStep(1);
                                            }}
                                            color="primary"
                                            inputProps={{ 'aria-describedby': 'declaration-text' }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ fontWeight: 500 }} id="declaration-text">
                                            I confirm that the information above is true and complete to the best of my knowledge and belief.
                                        </Typography>
                                    }
                                />
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
 
                {/* Submit Button */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/settings/hmrc')}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SubmitIcon />}
                        onClick={() => setConfirmDialog(true)}
                        disabled={!declarationChecked || submitting}
                        sx={{
                            background: 'linear-gradient(135deg, #059669, #047857)',
                            px: 4,
                            '&:hover': { background: 'linear-gradient(135deg, #047857, #065f46)' }
                        }}
                        aria-busy={submitting}
                    >
                        {submitting ? 'Submitting...' : 'Submit to HMRC'}
                    </Button>
                </Box>
 
                {/* Confirmation Dialog */}
                <Dialog
                    open={confirmDialog}
                    onClose={() => setConfirmDialog(false)}
                    aria-labelledby="confirm-dialog-title"
                    aria-describedby="confirm-dialog-description"
                >
                    <DialogTitle id="confirm-dialog-title">Confirm VAT Submission</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="confirm-dialog-description">
                            You are about to submit your VAT return to HMRC. This action cannot be undone.
                        </DialogContentText>
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                            <Typography variant="body2">
                                <strong>Period:</strong> {vatData && `${new Date(vatData.period_start).toLocaleDateString('en-GB')} - ${new Date(vatData.period_end).toLocaleDateString('en-GB')}`}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Net VAT:</strong> {vatData && formatCurrency(Math.abs(vatData.boxes.box5))} {vatData?.boxes.box5 >= 0 ? 'to pay' : 'refund'}
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
                        <Button onClick={handleSubmit} variant="contained" color="success">
                            Confirm & Submit
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};
 
export default VATSubmission;